import { type ServerResponse } from '@aytm/app/insights/dashboard/models/types.ServerResponse'
// lib
import railsPostFetch from '@aytm/lib/common/rails-post-fetch'
import { call, type CallEffect } from 'redux-saga/effects'

// stores
import staticStore from '@aytm/app/insights/dashboard/stores/static.store'
import { FetchError } from '@aytm/app/insights/dashboard/lib/FetchError'
import { retrySaga } from '@aytm/app/insights/dashboard/duckness/commonSagas/retrySaga'
import type { AccessKeyType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import { safelyExecuteSaga } from '@aytm/app/insights/dashboard/lib/safety'

export function* fetchAccessKeySaga({ sharedKey }: { sharedKey?: string } = {}): Generator<
  CallEffect<Response> | CallEffect<unknown>,
  AccessKeyType,
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  Response | unknown
> {
  const fetchAccessKeyURL = staticStore.urls.fetchAccessKey
  if (!fetchAccessKeyURL) {
    throw new FetchError('No "fetchAccessKey" endpoint URL', 'NO_QUERY_URL')
  }
  const response = (yield call(railsPostFetch, fetchAccessKeyURL, {
    body: JSON.stringify({ refresh: false, shared_key: sharedKey })
  })) as Response //as unknown as ServerResponse.AccessKeyResponse.RootType

  if (!response.ok) {
    throw new FetchError(
      (response as unknown as { error: string }).error || 'Error fetching access key',
      'FETCH_FAILED'
    )
  }

  type AccessKeyResponseType = { json: () => Promise<ServerResponse.AccessKeyResponse.RootType> }

  const jsonCallEffect = function* () {
    const result = (yield call<AccessKeyResponseType, 'json'>([
      response as AccessKeyResponseType,
      'json'
    ])) as ServerResponse.AccessKeyResponse.RootType
    return result
  }

  const [error, body] = (yield call(safelyExecuteSaga, jsonCallEffect)) as
    | [null, ServerResponse.AccessKeyResponse.RootType]
    | [Error]

  if (error || !body) {
    throw new FetchError('No access key returned, status: ' + response.statusText, 'INVALID_RESPONSE')
  }

  if ('error' in body && 'object' === typeof body.error) {
    if ('code' in body.error && 'access.denied' === body.error?.code) {
      throw new FetchError('Access denied', 'ACCESS_DENIED')
    } else if ('code' in body.error && 'fetch.failed' === body.error?.code) {
      throw new FetchError('Failed to fetch Access Key', 'INVALID_RESPONSE')
    }
    throw new FetchError(
      (body.error as unknown as { message?: string }).message || 'Error fetching access key',
      'INVALID_RESPONSE'
    )
  }

  if (!('key' in body)) {
    throw new FetchError('No access key returned', 'INVALID_RESPONSE')
  }

  return {
    code: body.key,
    roles: body.roles || [],
    expire: 'string' === typeof body.expire ? new Date(body.expire) : new Date()
  } as AccessKeyType
}

export const fetchAccessKeyRetried = retrySaga((iteration, lastError) => {
  if (FetchError.isFetchErrorWithCode(lastError, 'ACCESS_DENIED')) {
    return false
  }
  return iteration < 3
}, 'exponential')

export default fetchAccessKeySaga
